import {
  MAX_TESTCASE_NUMBER,
  PRELOADED_TEST_CASES_COUNT,
} from '~/features/x-ray/constants';
import { PromptModelTestCase } from '~/features/x-ray/types/deprecated';
import { Uuid } from '~/types/Uuid';

import {
  createPromptModelTestCase,
  CreatePromptModelTestCaseParams,
} from './createPromptModelTestCase';

const pendingBufferCreation = new Set<string>();
const TEST_CASE_CREATE_STAGGER_MS = 500;

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const getModelVersionKey = (modelId: Uuid, version: number): string =>
  `${modelId}-${version}`;

/**
 * Calculates how many test cases are needed to maintain the buffer.
 * @returns Object with neededTestCases count and whether buffer can be created
 */
export const calculateBufferNeeds = (
  currentTestCasesCount: number,
  activeTestCaseNumber: number,
): {
  neededTestCases: number;
  canCreateBuffer: boolean;
} => {
  const testCasesAhead = currentTestCasesCount - activeTestCaseNumber;
  const neededTestCases = PRELOADED_TEST_CASES_COUNT - testCasesAhead;
  const canCreateBuffer =
    neededTestCases > 0 &&
    currentTestCasesCount + neededTestCases <= MAX_TESTCASE_NUMBER;

  return { neededTestCases, canCreateBuffer };
};

export const createFirstTestCase = async (
  modelVersion: {
    testCases: PromptModelTestCase[];
    id?: Uuid;
    modelId?: Uuid;
    version: number;
  },
  highlightMethod: string,
): Promise<void> => {
  if (modelVersion.testCases.length === 0) {
    const modelId = modelVersion.id || modelVersion.modelId;
    if (!modelId) {
      throw new Error('Model ID is required to create test case');
    }
    const newTestCase = (await createPromptModelTestCase({
      modelId,
      version: modelVersion.version,
      highlightMethod,
      triggerExecution: false,
    })) as PromptModelTestCase;
    // Test case execution to be handled by consumer
    modelVersion.testCases.push(newTestCase);
  }
};

export const createBufferTestCases = async (
  modelId: Uuid,
  version: number,
  options: {
    currentTestCasesCount: number;
    activeTestCaseNumber: number;
    onAddTestCase?: (testCase: PromptModelTestCase) => void;
    highlightMethod: string;
  },
): Promise<void> => {
  const key = getModelVersionKey(modelId, version);

  if (pendingBufferCreation.has(key)) {
    return;
  }

  const {
    currentTestCasesCount,
    activeTestCaseNumber,
    onAddTestCase,
    highlightMethod,
  } = options;
  const { neededTestCases, canCreateBuffer } = calculateBufferNeeds(
    currentTestCasesCount,
    activeTestCaseNumber,
  );

  if (!canCreateBuffer) {
    return;
  }

  pendingBufferCreation.add(key);

  try {
    const testCaseParams: CreatePromptModelTestCaseParams = {
      modelId,
      version,
      highlightMethod,
      triggerExecution: false,
    };

    const createdTestCases = await Promise.all(
      Array.from({ length: neededTestCases }, async (_, index) => {
        if (index > 0) {
          await delay(TEST_CASE_CREATE_STAGGER_MS * index);
        }
        return createPromptModelTestCase(testCaseParams);
      }),
    );

    // onAddTestCase is provided when called from useMaintainTestCaseBuffer hook (UI already loaded)
    // Not provided when called from savePromptModelDraft/saveTableModelDraft (model creation, UI not loaded yet)
    if (onAddTestCase) {
      createdTestCases.forEach((testCase) => {
        if (testCase) {
          onAddTestCase(testCase as PromptModelTestCase);
        }
      });
    }
  } catch (error: unknown) {
    console.error('Failed to create buffer test cases:', error);
  } finally {
    pendingBufferCreation.delete(key);
  }
};
